import {
  DAY_END_MIN,
  DAY_START_MIN,
  DEFAULT_EST_MIN,
  SLOT_MIN,
} from "./constants";
import type { Energy } from "./types";

export interface SchedulableTask {
  id: string;
  estMin?: number | null;
  energy?: Energy | null;
  fromHour?: number | null; // minutes from midnight, null = unplaced
}

export interface AutoPlaceOptions {
  dayStartMin?: number;
  dayEndMin?: number;
}

interface Block {
  start: number;
  end: number;
}

function occupancy(estMin: number | null | undefined): number {
  return Math.max(SLOT_MIN, estMin ?? DEFAULT_EST_MIN);
}

/** Round an estimate up to a whole number of slots (min one slot). */
function durationOf(estMin: number | null | undefined): number {
  const e = estMin ?? DEFAULT_EST_MIN;
  return Math.max(SLOT_MIN, Math.ceil(e / SLOT_MIN) * SLOT_MIN);
}

/**
 * Auto-place scheduler — ported from the prototype's autoPlaceAll.
 *
 * Keeps already-timeboxed tasks fixed, then greedily packs the unplaced ones
 * (deep-energy first, then higher priority) into the earliest free gap of the
 * working day. Returns only the NEW placements as `{ [id]: fromHour }`; tasks
 * that don't fit before day-end are left unplaced.
 *
 * Pure: no Date, no mutation of inputs.
 */
export function autoPlace(
  tasks: ReadonlyArray<SchedulableTask & { pri?: number | null }>,
  opts: AutoPlaceOptions = {},
): Record<string, number> {
  const dayStart = opts.dayStartMin ?? DAY_START_MIN;
  const dayEnd = opts.dayEndMin ?? DAY_END_MIN;

  const blocks: Block[] = tasks
    .filter((t) => t.fromHour != null)
    .map((t) => ({ start: t.fromHour!, end: t.fromHour! + occupancy(t.estMin) }))
    .sort((a, b) => a.start - b.start);

  const unplaced = tasks
    .filter((t) => t.fromHour == null)
    .slice()
    .sort((a, b) => {
      const deep = (b.energy === "high" ? 1 : 0) - (a.energy === "high" ? 1 : 0);
      if (deep) return deep;
      return (a.pri ?? 2) - (b.pri ?? 2);
    });

  const placements: Record<string, number> = {};

  for (const task of unplaced) {
    const dur = durationOf(task.estMin);
    let cursor = dayStart;
    for (const b of blocks) {
      if (b.start - cursor >= dur) break; // gap before this block is big enough
      cursor = Math.max(cursor, b.end);
    }
    if (cursor + dur <= dayEnd) {
      placements[task.id] = cursor;
      const block: Block = { start: cursor, end: cursor + occupancy(task.estMin) };
      blocks.push(block);
      blocks.sort((a, b) => a.start - b.start);
    }
  }

  return placements;
}

/**
 * Snap/clamp a dropped or resized timebox to a valid start minute —
 * ported from the prototype's scheduleAt. Rounds to the slot grid and keeps
 * the whole block inside the working day.
 */
export function snapTimebox(
  fromMin: number,
  estMin: number | null | undefined,
  opts: AutoPlaceOptions = {},
): number {
  const dayStart = opts.dayStartMin ?? DAY_START_MIN;
  const dayEnd = opts.dayEndMin ?? DAY_END_MIN;
  const dur = Math.max(SLOT_MIN, estMin ?? DEFAULT_EST_MIN);
  let start = Math.round(fromMin / SLOT_MIN) * SLOT_MIN;
  start = Math.max(dayStart, Math.min(dayEnd - SLOT_MIN, start));
  if (start + dur > dayEnd) start = Math.max(dayStart, dayEnd - dur);
  return start;
}
